import type { TpPatientTreatmentBundle } from './types';

// Todd Baird — the Treatment Coordinator's cheat-sheet patient (staging fix 29
// §5). One person, he/him. Crown #14 + a deep cleaning, not yet booked: $2,130
// plan, ~$981 out of pocket, $1,240 of benefits left.
const AMARA: TpPatientTreatmentBundle = {
  context: {
    unscheduledValue: 2130,
    insurance: {
      carrier: 'Sun Life PPO',
      annualMax: 1500,
      used: 260,
      remaining: 1240,
      resetsIn: '94 days',
    },
    nextVisit: { date: 'Wed, May 28', time: '2:30 PM', reason: 'Hygiene + perio eval' },
    tenureYears: 10,
  },
  approach: {
    lead: 'Start with his question, then the crack,',
    tail: ' not the price',
    body: "Todd asked what the crack on #14 means and whether it can wait — answer that first. He shuts down when rushed and talks big decisions over with his wife Jane, so give him room and offer to text her the plan. Cost was the first no last time; lead with the tooth, not the number.",
    historicalPattern: '',
    dont: "Lead with the price, or re-pitch #14 as a brand-new ask — he's heard the number, and he says no when he feels rushed.",
  },
  quickRead: [
    { label: 'Decision style', value: 'Deliberate · talks it over with his wife Jane first', tone: 'neutral' },
    { label: 'When he replies', value: 'Reads in the evening · text preferred', tone: 'neutral' },
    { label: 'Language', value: 'Speaks Farsi at home · English fluent', tone: 'neutral' },
    { label: 'Cost sensitivity', value: 'Cost was his first no last time', tone: 'warning' },
    { label: 'Comfort', value: 'Nitrous for anxiety · never rush him', tone: 'positive' },
    { label: 'Trust level', value: 'High · patient since 2016 · 18 visits', tone: 'positive' },
  ],
  treatments: [
    {
      id: 'tx-perio',
      order: 'lead',
      code: 'D4341',
      tooth: null,
      description: 'Deep cleaning — lower front teeth',
      fee: 280,
      insurance: 224,
      patientOOP: 56,
      diagnosedAt: 'C. Lin (RDH) · found early pockets 2 Sep',
      checkedWith: 'checked with Sun Life 21 Sep',
      declineHistory: [],
      whyThisPosition:
        'The small, easy yes. $56 with his plan feels like a hygiene add-on, not a treatment decision — a good first step before the crown.',
      howToFrame:
        "Mention while he's in for hygiene. “While you're here — we found early pocketing on the lower front. A quick cleaning, about $56 with your plan, fifteen minutes.”",
      clinicalNow:
        'Early perio pockets deepen 1–2mm a year without treatment. Lower anteriors were flagged at his last hygiene.',
    },
    {
      id: 'tx-crown14',
      order: 'then',
      code: 'D2750',
      tooth: '#14',
      description: 'A crown on tooth #14',
      fee: 1850,
      insurance: 925,
      patientOOP: 925,
      diagnosedAt: 'Dr. Carter · first diagnosed Oct 2025, shown again 2 Sep',
      checkedWith: 'checked with Sun Life 21 Sep',
      xrayTint: '#E8EEF0',
      xrayCaption: 'His x-ray from 2 Sep — the crack on #14',
      declineHistory: [
        { date: 'Nov 2025', reason: 'Cost' },
        { date: 'Mar 2026', reason: 'Needs time · wife' },
      ],
      whyThisPosition:
        'This is the tooth he asked about. The old filling has cracked; a crown caps it before it splits. Answer his question, then the crown is the fix — not a fresh sell.',
      howToFrame:
        'Start with what the crack means, then the crown as the answer. One set of visits, not two. Offer to text the plan to Jane so they can look together.',
      clinicalNow:
        'The large old filling on #14 has cracked. Left alone a crack can spread until the tooth breaks — then it is a root canal or an extraction.',
      doctorNote: 'Distal margin is cracking — cap it before it splits.',
      doctorNoteBy: 'Dr. Carter',
    },
  ],
  objections: [
    {
      id: 'obj-cost',
      likelihood: 'high',
      trigger: 'Cost pause',
      when: 'Likely when the total crosses $1,000',
      practiceApproach:
        'Lead with his $1,240 of benefits left this year, then the crown on its own. Don’t stack both treatments into one number.',
    },
    {
      id: 'obj-spouse',
      likelihood: 'high',
      trigger: '“I need to talk to my wife”',
      when: 'Expected on the crown',
      practiceApproach:
        'Offer to text the plan link to Jane so they can look together. He decides in the evening — give him the room.',
    },
  ],
  worked: [
    { date: 'Apr 2025', item: 'Prophy + perio exam (~$220)', note: 'Accepted · on time for recall' },
    { date: 'Apr 2024', item: 'Composite #19 (~$285)', note: 'Accepted same-day · framed as preventive' },
    { date: 'Sep 2023', item: 'Prophy + bitewings (~$240)', note: 'Accepted · paired with hygiene' },
    { date: 'Mar 2023', item: 'Emergency · cracked #30 (~$980)', note: 'Accepted · treated the same day' },
  ],
  didntWork: [
    { date: 'Mar 2026', item: 'Crown #14 (~$925)', reason: 'Needs time · wife' },
    { date: 'Nov 2025', item: 'Crown #14 (~$925)', reason: 'Cost' },
  ],
  patternTakeaway:
    'Todd says yes to small, clearly-explained work. For the crown, answer his question, show him the x-ray, and give him the evening to talk it over with Jane. Never rush.',
};

const JAMES: TpPatientTreatmentBundle = {
  ...AMARA,
  context: {
    unscheduledValue: 4800,
    insurance: { carrier: 'Delta PPO', annualMax: 1500, used: 200, remaining: 1300, resetsIn: '120 days' },
    nextVisit: { date: 'Fri, Jun 6', time: '9:00 AM', reason: 'Invisalign check' },
    tenureYears: 3,
  },
  approach: {
    lead: 'Lead with aligner check-in,',
    tail: ' then full Invisalign case',
    body: 'James is motivated on Invisalign — focus today’s conversation on scan progress and payment phasing.',
    historicalPattern: 'Accepts phased plans when monthly cost is under $200.',
  },
  treatments: AMARA.treatments.slice(0, 2),
  objections: AMARA.objections.slice(0, 2),
  worked: AMARA.worked.slice(0, 2),
  didntWork: [],
  patternTakeaway: 'Responds to clear timelines and tech explanations.',
};

const SARA: TpPatientTreatmentBundle = {
  ...AMARA,
  context: {
    unscheduledValue: 890,
    insurance: { carrier: 'None on file', annualMax: 0, used: 0, remaining: 0, resetsIn: '—' },
    nextVisit: { date: 'Tue, Jun 3', time: '11:00 AM', reason: 'Whitening consult' },
    tenureYears: 0,
  },
  approach: {
    lead: 'Lead with whitening for the wedding,',
    tail: ' keep scope small',
    body: 'New patient — wedding in 8 weeks. Single clear offer today.',
    historicalPattern: 'No history yet — treat as first presentation.',
  },
  treatments: AMARA.treatments.slice(0, 1),
  objections: AMARA.objections.slice(0, 1),
  worked: [],
  didntWork: [],
  patternTakeaway: 'First visit — establish trust before multi-item plans.',
};

const TOM: TpPatientTreatmentBundle = {
  ...AMARA,
  context: {
    unscheduledValue: 0,
    insurance: { carrier: 'MetLife DHMO', annualMax: 1000, used: 890, remaining: 110, resetsIn: '90 days' },
    nextVisit: { date: '—', time: '—', reason: 'Recall overdue' },
    tenureYears: 15,
  },
  approach: {
    lead: 'Lead with recall hygiene only,',
    tail: ' defer elective work',
    body: 'Tom is overdue on recall — book prophy first; pre-med required day-of.',
    historicalPattern: 'Accepts recall when reached by phone; texts less reliable.',
  },
  treatments: AMARA.treatments.slice(0, 1),
  objections: AMARA.objections.slice(0, 1),
  worked: AMARA.worked,
  didntWork: AMARA.didntWork,
  patternTakeaway: 'Pre-med + phone outreach — see playbook.',
};

// Orville Allison — p001, the Invisalign patient (amendment 34 §4). His plan is
// not Todd's crown: a small filling already done, and the clear-aligner
// opportunity his benefit would cover. Delta Dental PPO.
const ORVILLE: TpPatientTreatmentBundle = {
  ...AMARA,
  context: {
    unscheduledValue: 5200,
    insurance: {
      carrier: 'Delta Dental PPO',
      annualMax: 1500,
      used: 40,
      remaining: 1460,
      resetsIn: '94 days',
    },
    nextVisit: { date: 'Today', time: '5:00 PM', reason: 'Filling #30' },
    tenureYears: 7,
  },
  approach: {
    lead: 'He asked what aligners would cost —',
    tail: ' answer that, then the consult',
    body: "Orville has never used his orthodontic benefit and asked the Invis-Line what clear aligners would cost with his insurance. Answer the cost question plainly, then offer the consult that gives the exact number. His partner Dana is in on the decision — offer to include them.",
    historicalPattern: '',
    dont: 'Quote a case fee or promise a timeline — a consult gives the real number.',
  },
  quickRead: [
    { label: 'Decision style', value: 'Talks it over with his partner Dana', tone: 'neutral' },
    { label: 'When he replies', value: 'Reads in the evening · text preferred', tone: 'neutral' },
    { label: 'Language', value: 'English', tone: 'neutral' },
    { label: 'Cost sensitivity', value: 'Cost was the open question on aligners', tone: 'warning' },
    { label: 'Benefit', value: 'Orthodontic benefit never used', tone: 'positive' },
    { label: 'Trust level', value: 'Established · patient since 2018 · 11 visits', tone: 'positive' },
  ],
  treatments: [
    {
      id: 'tx-aligners',
      order: 'lead',
      code: 'D8090',
      tooth: null,
      description: 'Clear aligner treatment',
      fee: 5200,
      insurance: 1500,
      patientOOP: 3700,
      diagnosedAt: 'Interest from the Invis-Line · 24 Sep',
      checkedWith: 'ortho benefit unused — a consult confirms the exact number',
      declineHistory: [],
      whyThisPosition:
        'He asked what aligners cost. A consult gives him the exact number and the scan; his unused ortho benefit comes off the top.',
      howToFrame:
        'Answer the cost question, then offer the consult. “Your plan has orthodontic benefit you’ve never used — a quick consult with Dr. Carter gives you the exact number for your case.”',
      clinicalNow:
        'Mild crowding he mentioned on the Invis-Line. A consult and scan confirm suitability.',
    },
  ],
  objections: [
    {
      id: 'obj-cost',
      likelihood: 'high',
      trigger: 'Cost pause',
      when: 'Likely on the aligner total',
      practiceApproach:
        'Lead with his unused orthodontic benefit, then the consult that gives the exact number. Don’t quote a case fee cold.',
    },
    {
      id: 'obj-partner',
      likelihood: 'medium',
      trigger: '“I need to talk to Dana”',
      when: 'Expected before booking',
      practiceApproach:
        'Offer to text the consult details so they can look together. He decides in the evening — give him the room.',
    },
  ],
  worked: [
    { date: 'Aug 2025', item: 'Filling #30 (~$40 OOP)', note: 'Accepted · same-day' },
    { date: 'Feb 2025', item: 'Prophy + bitewings (~$210)', note: 'Accepted · on time for recall' },
  ],
  didntWork: [],
  patternTakeaway:
    'Orville is Ready for a nudge on aligners — he asked the cost himself. Answer it, offer the consult, and include Dana. Never quote a case fee cold.',
};

export const PP_TREATMENT_BY_PATIENT: Record<string, TpPatientTreatmentBundle> = {
  'amara-reza': AMARA,
  'james-chen': JAMES,
  'sara-malik': SARA,
  'tom-rivera': TOM,
  p001: ORVILLE,
};
